import { Injectable } from "@angular/core";
import { environment } from "../../environments/environment";

/**
 * Typed request/response bridge to the Flip mobile app's native shell (store purchases, storefront,
 * system browser, subscription management). Contract: docs/native-bridge-contract.md.
 *
 * Inside the app, requests go out via `window.ReactNativeWebView.postMessage` and results come back
 * through `window.__flipBridgeResult`. In a normal browser nothing is posted and every request
 * resolves with `{ ok: false, error: { code: "unsupported" } }` - it never throws or rejects.
 */

export const NATIVE_BRIDGE_VERSION = 1;

export type NativeBridgeType =
  | "getStorefront"
  | "purchase"
  | "restorePurchases"
  | "openUrl"
  | "beginAlternativeBilling"
  | "openSubscriptionManagement"
  | "presentOfferCodeRedemption";

export type NativeBridgeErrorCode =
  | "unsupported"
  | "invalid_payload"
  | "cancelled"
  | "failed"
  | "offer_unavailable"
  | "timeout"
  | "not_implemented";

export interface NativeBridgeError {
  code: NativeBridgeErrorCode | string;
  message?: string;
}

export interface NativeBridgeResponse<T = any> {
  v?: number;
  type: string;
  requestId: string;
  ok: boolean;
  payload?: T;
  error?: NativeBridgeError;
}

export type NativeBridgeResult<T = any> =
  | { ok: true; payload: T }
  | { ok: false; error: NativeBridgeError };

interface Pending {
  type: NativeBridgeType;
  resolve: (r: NativeBridgeResult) => void;
  timer: any;
}

const DEFAULT_TIMEOUT_MS = 60 * 1000;
const PURCHASE_TIMEOUT_MS = 5 * 60 * 1000;

@Injectable({ providedIn: "root" })
export class NativeBridgeService {
  private pending = new Map<string, Pending>();
  private seq = 0;

  constructor() {
    if (typeof window !== "undefined") {
      (window as any).__flipBridgeResult = (msg: unknown) => this.handleResponse(msg);

      // Dev-only console hook for exercising the bridge on a device build (Chrome inspect /
      // Safari Web Inspector): `await flipBridge.getStorefront()`, `flipBridge.request("openUrl",
      // {url: "https://example.com"})`, etc. Absent from staging/production builds.
      if (!environment.production) {
        (window as any).flipBridge = this;
        console.info("[native-bridge] dev hook ready: window.flipBridge (inApp=" + this.isInApp() + ")");
      }
    }
  }

  isInApp(): boolean {
    const rn = typeof window !== "undefined" ? (window as any).ReactNativeWebView : null;
    return !!rn && typeof rn.postMessage === "function";
  }

  getStorefront() {
    return this.request<{ country: string }>("getStorefront");
  }

  purchase(planId: string, userId: string) {
    return this.request<{ status: "success"; transactionRef?: string }>("purchase", {
      planId,
      userId,
    });
  }

  restorePurchases(options?: { sync?: boolean }) {
    // `transactionRefs` (FLIP-523, iOS only) are the signed transactions of the restored
    // subscriptions, for the server to validate; absent where the store has none to hand over.
    // `sync: false` only reads what the device already holds (no App Store sync, so no Apple ID
    // prompt); app builds that predate the option ignore it and sync as before.
    return this.request<{ restored: number; transactionRefs?: string[] }>(
      "restorePurchases",
      options?.sync === false ? { sync: false } : undefined
    );
  }

  openUrl(url: string) {
    return this.request<{}>("openUrl", { url });
  }

  /**
   * Android Billing Choice (FLIP-525): the user picked the developer's own billing (Stripe).
   * Native mints Google's external transaction token and shows Play's information dialog; the
   * token is sent to the server with the checkout so the transaction can be reported to Google.
   */
  beginAlternativeBilling() {
    return this.request<{ externalTransactionToken: string }>("beginAlternativeBilling");
  }

  openSubscriptionManagement() {
    return this.request<{}>("openSubscriptionManagement");
  }

  presentOfferCodeRedemption() {
    return this.request<{}>("presentOfferCodeRedemption");
  }

  request<T = any>(type: NativeBridgeType, payload?: object): Promise<NativeBridgeResult<T>> {
    if (!this.isInApp()) {
      return Promise.resolve({ ok: false, error: { code: "unsupported" } });
    }

    const requestId = `${Date.now().toString(36)}-${(++this.seq).toString(36)}`;
    // The user is inside a system sheet (Apple's purchase or offer-code sheet) for as long as they take.
    const timeoutMs =
      type === "purchase" || type === "presentOfferCodeRedemption" ? PURCHASE_TIMEOUT_MS : DEFAULT_TIMEOUT_MS;

    return new Promise<NativeBridgeResult<T>>((resolve) => {
      const timer = setTimeout(() => {
        this.pending.delete(requestId);
        resolve({ ok: false, error: { code: "timeout" } });
      }, timeoutMs);
      this.pending.set(requestId, { type, resolve: resolve as any, timer });

      try {
        (window as any).ReactNativeWebView.postMessage(
          JSON.stringify({ v: NATIVE_BRIDGE_VERSION, type, requestId, payload: payload ?? {} })
        );
      } catch (e) {
        clearTimeout(timer);
        this.pending.delete(requestId);
        resolve({ ok: false, error: { code: "failed", message: String(e) } });
      }
    });
  }

  /** Receiver for native replies. Malformed or unmatched messages are ignored. */
  private handleResponse(msg: unknown): void {
    let res: any = msg;
    if (typeof msg === "string") {
      try {
        res = JSON.parse(msg);
      } catch {
        return;
      }
    }
    if (!res || typeof res.requestId !== "string") return;

    const entry = this.pending.get(res.requestId);
    if (!entry) return;

    clearTimeout(entry.timer);
    this.pending.delete(res.requestId);

    if (res.ok === true) {
      entry.resolve({ ok: true, payload: res.payload ?? {} });
    } else {
      const err = res.error && typeof res.error.code === "string" ? res.error : { code: "failed" };
      entry.resolve({ ok: false, error: err });
    }
  }
}
